import {
    GuestLoginButton,
    Loading,
} from "components";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";

import { getServiceList } from "apis";
import { ServiceCategory } from "types";
import { ServiceDTO } from "types/DTOs/service/service.dto";

const categories: { emoji: string; label: string; value: ServiceCategory | undefined }[] = [
    { emoji: "✨", label: "전체", value: undefined },
    { emoji: "💬", label: "수다·티타임", value: ServiceCategory.CHAT },
    { emoji: "🎮", label: "게임·듀오", value: ServiceCategory.GAME },
    { emoji: "🔮", label: "타로·운세", value: ServiceCategory.TAROT },
    { emoji: "💡", label: "고민·피드백", value: ServiceCategory.FEEDBACK },
    { emoji: "💻", label: "랜선 스터디", value: ServiceCategory.STUDY },
    { emoji: "✨", label: "자유주제", value: ServiceCategory.FREE },
];

const getCategoryColor = (category: ServiceCategory): string => {
    switch (category) {
        case ServiceCategory.CHAT: return "bg-pink-50 text-pink-600";
        case ServiceCategory.GAME: return "bg-purple-50 text-purple-600";
        case ServiceCategory.TAROT: return "bg-violet-50 text-violet-600";
        case ServiceCategory.FEEDBACK: return "bg-amber-50 text-amber-600";
        case ServiceCategory.STUDY: return "bg-blue-50 text-blue-600";
        case ServiceCategory.FREE: return "bg-emerald-50 text-emerald-600";
        default: return "bg-achromatic-100 text-achromatic-500";
    }
};

const isSlotAvailable = (s: any): boolean => {
  if (s.isBooked || !s.date) return false;
  const timeStr = s.endTime || s.startTime || "23:59:59";
  const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
  return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
};

export default function Page() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>(undefined);

    const { data: servicesData, isLoading } = useQuery(
        ["guestServices", selectedCategory],
        () => getServiceList(selectedCategory)
    );

    const rawServices: ServiceDTO[] = servicesData?.data?.data ?? [];
    const services: ServiceDTO[] = rawServices.filter((s) => {
        const availableCount = s.schedules?.filter(isSlotAvailable).length ?? 0;
        return availableCount > 0;
    });

    const filteredServices = searchTerm.trim()
        ? services.filter(
            (s) =>
                s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : services;

    return (
        <div className="animate-fadeUp mobile:pb-20">
            {/* CTA Card */}
            <div className="bg-white rounded-2xl p-6 mb-5 mobile:rounded-none mobile:mb-4">
                <h1 className="text-xl font-extrabold text-achromatic-900 mb-1">
                    시간과 경험을 거래해 보세요 ✨
                </h1>
                <p className="text-[13px] text-achromatic-400 mb-4">가입하고 다양한 서비스를 이용해 보세요</p>
                <button
                    onClick={() => navigate('/login')}
                    className="px-5 py-2.5 bg-primary-500 text-white rounded-xl text-[13px] font-bold hover:bg-primary-600 transition-colors"
                >
                    시작하기
                </button>
            </div>

            {/* Category pills */}
            <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1 mobile:px-4">
                {categories.map((cat) => (
                    <button
                        key={cat.label}
                        onClick={() => setSelectedCategory(cat.value)}
                        className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                            selectedCategory === cat.value
                                ? "bg-achromatic-900 text-white"
                                : "bg-white text-achromatic-500 hover:text-achromatic-700"
                        }`}
                    >
                        <span>{cat.emoji}</span>
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Service grid */}
            {isLoading && <Loading />}
            {!isLoading && filteredServices.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center">
                    <span className="text-4xl block mb-3">📭</span>
                    <p className="text-sm font-semibold text-achromatic-800 mb-1">등록된 서비스가 없습니다</p>
                    <p className="text-xs text-achromatic-400">구매 가능한 시간대가 설정된 서비스만 여기에 표시됩니다</p>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 mobile:px-4">
                {filteredServices.map((service) => {
                    const availableSlots = service.schedules?.filter(isSlotAvailable).length ?? 0;
                    return (
                        <div
                            key={service.id}
                            className="bg-white rounded-2xl p-5 cursor-pointer hover:shadow-card-hover transition-all duration-200 group"
                            onClick={() => navigate("/login")}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${getCategoryColor(service.category)}`}>
                                    {service.category}
                                </span>
                                <span className={`text-[11px] font-semibold ${
                                    availableSlots > 0 ? "text-green-500" : "text-achromatic-300"
                                }`}>
                                    {availableSlots > 0 ? `${availableSlots}개 가능` : "마감"}
                                </span>
                            </div>
                            <h3 className="text-[15px] font-bold text-achromatic-900 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">{service.title}</h3>
                            <p className="text-xs text-achromatic-400 line-clamp-2 mb-4">{service.description}</p>
                            <div className="flex justify-between items-center pt-3 border-t border-achromatic-50">
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-achromatic-100 rounded-full flex items-center justify-center text-[10px] font-bold text-achromatic-500">
                                        {service.host?.name?.charAt(0) ?? "?"}
                                    </div>
                                    <span className="text-xs text-achromatic-500">{service.host?.name ?? "호스트"}</span>
                                </div>
                                <div>
                                    <span className="text-[15px] font-extrabold text-achromatic-900">{service.price.toLocaleString()}</span>
                                    <span className="text-[11px] text-achromatic-400 ml-0.5">원</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
